import {HistoryEntry} from './history-types';
import {
    FetchNextPageCompletedAction,
    SearchCompletedAction,
    SearchFacetsCompletedAction,
    SearchPageAction
} from './search-types';
import {
    HibuSearchRequest,
    HibuSearchResponse,
    ProdDocument,
    ProductDocument,
    SearchExportFormat,
    SearchResult,
    SortType,
    TabType
} from '../search-types';
import {DeepReadonly} from 'utility-types';
import {Dispatch, GetState, State, TempThunkAction} from '../../app-types';
import {ThunkAction} from 'redux-thunk';
import {
    FacetGroup,
    FacetValuesSortOrder,
    HibuEqualsFilter,
    HibuRangeFilter,
    ReduxSelectedFacetValueByFieldName
} from '../../facets/facetTypes';
import {batch} from 'react-redux';
import {
    formatHits,
    getActualQueryString,
    getSearchRequest,
    handleSearchError,
    isBlacklistedForAutoCorrection,
    mapDocument
} from '../search.utils';
import {doFetchFacets, doFetchNextDocuments, doSearchRequest} from '../../services/search-service';
import {scrollToTop} from '../../utils/utils';
import {SEARCH_RESULTS_LIST_CLASS} from '../search-results/search-result-list/search-results-list';
import {searchPageAttributesMap, SearchPageType} from '../search-config';
import {buildEncodedUrlPath} from '../url-path-builder';
import {ClickableElement} from '../search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {PreviewDocument} from '../search-results/search-preview/doc-preview-section/docPreviewRow-types';
import {PdfHighlight} from '../../common/pdf-viewer/PdfViewer.types';
import {
    fetchHighlightedDocument,
    fetchPdfDocument,
    fetchPlainDocument
} from '../search-results/search-preview/doc-preview-section/docPreviewRow-service';
import {SpotlightTagType} from '../../config/config-types';

export const searchHistoryLoadedAction = (
    searchPageType: SearchPageType,
    entries: HistoryEntry[]
): SearchPageAction => {
    return {
        type: 'SEARCH_HISTORY_LOADED',
        searchPageType,
        entries,
    };
};

export const searchHistoryClearedAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        type: 'SEARCH_HISTORY_LOADED',
        searchPageType,
        entries: [],
    };
};

export const searchScrollButtonVisibilityChanged = (
    searchPageType: SearchPageType,
    showScrollButton: boolean
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_SCROLL_BUTTON_VISIBILITY_CHANGED',
        showScrollButton: showScrollButton,
    };
};

export const searchResultSetExpandedDocumentAction = (searchPageType: SearchPageType, doc: DeepReadonly<ProdDocument> | undefined): SearchPageAction => ({
    searchPageType: searchPageType,
    type: 'SEARCH_RESULT_SET_EXPANDED_DOCUMENT',
    doc: doc,
});

// FACETS
export const updateFacetAction = (
    searchPageType: SearchPageType,
    facetGroupFieldName: string,
    newSelectedFacetValues: string[],
    containerFacets: string[]
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_FACETS_UPDATE',
        fieldName: facetGroupFieldName,
        newSelectedFacetValues: newSelectedFacetValues,
        containerFacets: containerFacets
    };
};

export const updateAllFacetsAction = (
    searchPageType: SearchPageType,
    facetFilters: ReduxSelectedFacetValueByFieldName,
    containerFacets: string[]
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'FACETS_ALL_UPDATE',
        facetFilters: facetFilters,
        containerFacets: containerFacets
    };
};

export const expandCollapseFacetGroupAction = (
    fieldName: string,
    searchPageType: SearchPageType
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'EXPAND_COLLAPSE_FACET_GROUP',
        fieldName: fieldName,
    };
};

export const expandFacetGroupAction = (
    fieldName: string,
    searchPageType: SearchPageType
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'EXPAND_FACET_GROUP',
        fieldName: fieldName,
    };
};

export const facetValuesSortOrderChangeAction = (
    sortField: FacetValuesSortOrder,
    searchPageType: SearchPageType
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'FACET_VALUES_SORT_ORDER_CHANGED',
        facetSortField: sortField,
    };
};

// SEARCH

export const changeInDocumentQueryAction = (inDocumentQuery: string | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SEARCH_CHANGE_IN_DOCUMENT_QUERY',
        inDocumentQuery: inDocumentQuery,
    };
};

export const searchSourceSwitchedAction = (searchPageType: SearchPageType, name: string): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_SOURCE_SWITCHED',
        name: name,
    };
};

export const exportRequestedAction = (searchPageType: SearchPageType, exportFormat: SearchExportFormat): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'EXPORT_REQUESTED',
        reportFormat: exportFormat,
    };
};

export const fetchNextPageStartedAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'FETCH_NEXT_PAGE_STARTED',
    };
};

export const sortChangeAction = (searchPageType: SearchPageType, eventKey: SortType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'RESULT_SORT_CHANGED',
        sortType: eventKey,
    };
};

export const startSearchAction = (searchPageType: SearchPageType, query = ''): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_STARTED',
        query: query,
    };
};
export const startSearchFacetAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_FACETS_STARTED'
    };
};

export const searchResetFacetsAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_RESET_FACETS',
    };
};

export const addBookmarkAction = (searchPageType: SearchPageType, doc: ProdDocument): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_ADD_BOOKMARK',
        doc: doc,
    };
};

export const removeBookmarkAction = (searchPageType: SearchPageType, doc: ProdDocument): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_REMOVE_BOOKMARK',
        doc: doc,
    };
};

export function setSearchQueryFieldsAction(
    searchPageType: SearchPageType,
    containerQueries: string[]
): SearchPageAction {
    return {
        type: 'SET_SEARCH_QUERY_FIELDS',
        searchPageType: searchPageType,
        queryFields: containerQueries,
    };
}

export const searchCompletedAction = (
    searchResult: Partial<SearchResult>,
    searchPageType: SearchPageType
): SearchCompletedAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_COMPLETED',
        searchResult: searchResult,
    };
};

export const searchFacetsCompletedAction = (
    searchResult: Partial<SearchResult>,
    searchPageType: SearchPageType
): SearchFacetsCompletedAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_FACETS_COMPLETED',
        searchResult: searchResult,
    };
};

export const fetchNextPageCompletedAction = (
    docs: ProdDocument[]
): FetchNextPageCompletedAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'FETCH_NEXT_PAGE_COMPLETED',
        docs: docs
    };
};

export const expandFacetListAction = (expanded: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'EXPAND_FACET_LIST',
        expanded: expanded
    };
};

export const expandDocumentDetailsAction = (expanded: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'EXPAND_DOCUMENT_DETAILS',
        expanded: expanded
    };
};

export const setPreviewDocumentAction = (previewDocument: PreviewDocument | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'PREVIEW_DOCUMENT',
        previewDocument: previewDocument,
    };
};

export const setPreviewDocumentWithHighlightsAction = (previewDocumentWithHighlights: PreviewDocument | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'PREVIEW_DOCUMENT_WITH_HIGHLIGHTS',
        previewDocumentWithHighlights: previewDocumentWithHighlights
    };
};

export const showAnnotations = (showAnnotations: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SHOW_ANNOTATIONS',
        showAnnotations: showAnnotations
    };
};

export const setDocumentQueryHighlights = (queryHighlights: PdfHighlight[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_QUERY_HIGHLIGHTS',
        documentAnnotations: queryHighlights
    };
};
export const setDocumentSelectedTags = (selectedTags: PdfHighlight[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_SELECTED_TAGS',
        documentSelectedTags: selectedTags
    };
};

export const switchResultDetailsTabAction = (resultDetailsTab: TabType): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SWITCH_RESULT_DETAILS_TAB',
        resultDetailsTab: resultDetailsTab
    };
};

export const setCurrentPageNumberAction = (newPageNumber: number | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_DOCUMENT_PAGE_CHANGED',
        newPageNumber: newPageNumber
    };
};

export const setCurrentPageRotationAction = (newRotation: number): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CURRENT_PAGE_ROTATED',
        rotation: newRotation
    };
};

export const setDocumentAllPagesRotation = (rotations: number[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CURRENT_DOCUMENT_ROTATIONS',
        rotations: rotations
    };
};

export const setScrollTargetAction = (scrollTarget: ClickableElement): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SCROLL_TARGET_CHANGED',
        newPageNumber: scrollTarget.pageNumber,
        scrollTarget: scrollTarget
    };
};

export const resetTransformAction = (resetTransform: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'RESET_TRANSFORM',
        resetTransform: resetTransform,
    };
};

export const setHighlightIdAction = (highlightId: string | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_HIGHLIGHT_ID',
        highlightId: highlightId
    };
};

export const setSelectedDocumentNumberOfPages = (numPages: number): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_DOCUMENT_CHANGED',
        numberOfPages: numPages
    };
};

export const setUseAutoCorrection = (useAutoCorrection: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'UPDATE_AUTO_CORRECTION',
        useAutoCorrection: useAutoCorrection
    };
};

export function setAvailableSortOptionsAction(
    sortTypes: SortType[]
): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_AVAILABLE_SORT_OPTIONS',
        sortTypes: sortTypes
    };
}

export function setIsLoadingDocumentAction(
    isLoading: boolean
): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'IS_LOADING_DOCUMENT',
        isLoading: isLoading
    };
}

export function changeUrlAction(searchPageType: SearchPageType, history: History | null): any {
    return (dispatch: Dispatch, getState) => {
        changeUrl(getState, searchPageType, history);
    };
}

export const sortResults = (searchPageType: SearchPageType, eventKey: SortType, history: History) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        dispatch(sortChangeAction(searchPageType, eventKey));
        return doSearch(dispatch, history, getState);
    };
};

export const filterFacets = (
    searchPageType: SearchPageType,
    facetGroup: FacetGroup,
    newSelectedFacetValues: string[],
    history: History,
    configFacets: string[]): TempThunkAction => {
    return (dispatch: Dispatch, getState) => {
        dispatch(updateFacetAction(searchPageType, facetGroup.fieldName, newSelectedFacetValues, configFacets));
        dispatch(fetchFacets(searchPageType, getActualQueryString(getState().search[searchPageType])));
        return doSearch(dispatch, history, getState);
    };
};

export const clearSelectedFacets = (searchPageType: SearchPageType, history: History): TempThunkAction => {
    return (dispatch: Dispatch, getState) => {
        dispatch(searchResetFacetsAction(searchPageType));
        dispatch(fetchFacets(searchPageType, getActualQueryString(getState().search[searchPageType])));
        return doSearch(dispatch, history, getState);
    };
};

function doSearch(dispatch: Dispatch, history: History, getState: GetState) {
    return dispatch(search(SearchPageType.BASIC, history, getActualQueryString(getState().search[SearchPageType.BASIC]), false, false));
}

export const openNewDocument = (doc: ProdDocument | undefined) => {
    return (dispatch: Dispatch): void => {
        dispatch(setPreviewDocumentAction(undefined));
        dispatch(setPreviewDocumentWithHighlightsAction(undefined));
        dispatch(setCurrentPageNumberAction(1));
        dispatch(setDocumentSelectedTags([]));
        dispatch(fetchDocumentDetails(doc));
    };
};

export const fetchDocumentDetails = (sourceDoc: ProdDocument | undefined) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        const searchPageType = SearchPageType.BASIC;
        const settingsContainerName = getState().settings.containerName;
        if (sourceDoc) {
            dispatch(setIsLoadingDocumentAction(true));
            fetchPlainDocument(searchPageType, sourceDoc.id, settingsContainerName)
                .then(doc => {
                    if (doc) {
                        const productDocument = mapDocument(doc, [], sourceDoc.duplicates);
                        dispatch(searchResultSetExpandedDocumentAction(searchPageType, productDocument));
                        dispatch(setIsLoadingDocumentAction(false));
                    }
                });
        }
    };
};

export const doFetchPdfDocumentPreviews = (selectedDocument: ProductDocument | undefined, showAnnotations: boolean,
                                           searchPageType: SearchPageType.BASIC, resultFor: string, query: string,
                                           settingsContainerName: string, spotlightTagTypes: SpotlightTagType[]) => {
    return (dispatch: Dispatch): void => {
        if (selectedDocument) {
            dispatch(setIsLoadingDocumentAction(true));
            const previewLink = selectedDocument.metadata.previewLink_string;
            if (showAnnotations) {
                fetchHighlightedDocument(searchPageType, resultFor || query, selectedDocument.id, settingsContainerName, true, spotlightTagTypes, previewLink)
                    .then(fetchDocument => dispatch(setPreviewDocumentWithHighlightsAction(fetchDocument)))
                    .finally(() => dispatch(setIsLoadingDocumentAction(false)));
            } else if (query) {
                fetchHighlightedDocument(searchPageType, selectedDocument.id,
                    resultFor || query, settingsContainerName, false, [],
                    previewLink)
                    .then(fetchDocument => dispatch(setPreviewDocumentAction(fetchDocument)))
                    .finally(() => dispatch(setIsLoadingDocumentAction(false)));
            } else {
                fetchPdfDocument(searchPageType, previewLink)
                    .then(fetchDocument => dispatch(setPreviewDocumentAction(fetchDocument)))
                    .finally(() => dispatch(setIsLoadingDocumentAction(false)));
            }
        }
    };
};


export const search = (
    searchPageType: SearchPageType,
    history: History | null,
    query?: string,
    isSubmittingNewQuery = true,
    isUsingAutoCorrection = true) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        // scroll to top before executing the task. Prevents the infinite scroll list from loading a huge amount of items
        scrollToTop(SEARCH_RESULTS_LIST_CLASS);
        dispatch(startSearchAction(searchPageType, query));
        const {
            request,
            updatedIsUsingAutoCorrection
        } = prepareAndGetSearchRequest(getState, query, searchPageType, isUsingAutoCorrection);

        if (history) {
            changeUrl(getState, searchPageType, history);
        }
        const replaceUrl = (searchExpression: string) => {
            changeUrl(getState, searchPageType, history, searchExpression);
        };

        dispatch(resetResultDetails());
        dispatch(setUseAutoCorrection(updatedIsUsingAutoCorrection));
        return dispatch(doSearchRequest(searchPageType, request, isSubmittingNewQuery, true, replaceUrl));
    };
};

export const fetchFacets = (
    searchPageType: SearchPageType,
    query?: string,
    isUsingAutoCorrection = true) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        dispatch(startSearchFacetAction(searchPageType));
        const {request} = prepareAndGetSearchRequest(getState, query, searchPageType, isUsingAutoCorrection);
        return dispatch(doFetchFacets(searchPageType, request));
    };
};

export const fetchNextPage = (searchPageType: SearchPageType, useAutoCorrection: boolean): TempThunkAction => {
    return (dispatch: Dispatch, getState: GetState) => {
        dispatch(fetchNextPageStartedAction(searchPageType));
        const {request} = prepareAndGetSearchRequest(getState, undefined, searchPageType, useAutoCorrection);
        dispatch(setUseAutoCorrection(useAutoCorrection));
        dispatch(doFetchNextDocuments(request))
            .then((data?: HibuSearchResponse) => {
                if (!data) {
                    return; // Fetch was aborted. The next response will finish updating the state.
                }
                return dispatch(fetchNextPageCompletedAction(formatHits(data.hits)));
            })
            .catch((error) => {
                return handleSearchError(error, dispatch);
            });
    };
};

export const searchSavedSearch = (searchPageType: SearchPageType, request: HibuSearchRequest, history: History, containerFacets: string[]) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        // scroll to top before executing the task. Prevents the infinite scroll list from loading a huge amount of items
        scrollToTop(SEARCH_RESULTS_LIST_CLASS);
        dispatch(startSearchAction(searchPageType, request.query));
        batch(() => {
            Object.entries(request.filters).forEach(([filterKey, filters]) => {
                const newSelectedFacetValues: string[] = [];

                filters.forEach((filter) => {
                    if (filter.type === 'range') {
                        const {gte, lt} = filter as HibuRangeFilter;
                        newSelectedFacetValues.push(gte, lt);
                    } else if (filter.type === 'equals') {
                        newSelectedFacetValues.push((filter as HibuEqualsFilter).value);
                    }
                });

                dispatch(updateFacetAction(searchPageType, filterKey, newSelectedFacetValues, containerFacets));
                dispatch(expandFacetGroupAction(filterKey, searchPageType));
            });
            changeUrl(getState, searchPageType, history);
            dispatch(resetResultDetails());
            return dispatch(doSearchRequest(searchPageType, request, true, true));
        });
    };
};

function prepareAndGetSearchRequest(getState: () => State, query: string | undefined, searchPageType: SearchPageType, isUsingAutoCorrection: boolean): {
    request: HibuSearchRequest,
    updatedIsUsingAutoCorrection: boolean
} {
    const state = getState();
    query = query !== undefined ? query : state.search[searchPageType].query;
    const updatedIsUsingAutoCorrection = isUsingAutoCorrection && !isBlacklistedForAutoCorrection(query);
    const request = getSearchRequest(searchPageType, getState, query, isUsingAutoCorrection);
    return {request, updatedIsUsingAutoCorrection: updatedIsUsingAutoCorrection};
}

export const changeUrl = (getState: GetState, searchPageType: SearchPageType, history: any, newSearchExpression = ''): void => {
    const replaceSearchExpression: boolean = newSearchExpression !== '';
    const searchExpression: string = replaceSearchExpression ? newSearchExpression : getState().search[searchPageType].query;
    const sortField = getState().search[searchPageType].currentSortType.field;
    const facetFilter = getState().search[searchPageType].facets.selectedFacetValuesByFieldName;
    const facetGroup = getState().search[searchPageType].facets.facetGroups.byKey;
    const openTab = getState().search[searchPageType].openResultTab;
    const encodedUrlPath: string = buildEncodedUrlPath(searchPageAttributesMap[searchPageType].route,
        searchExpression, sortField, facetFilter, facetGroup, openTab);
    replaceSearchExpression ? history.replace(encodedUrlPath) : history.push(encodedUrlPath);
};

export const resetResultDetails = (): ThunkAction<void, State, unknown, SearchPageAction> => {
    return (dispatch: Dispatch, getState: () => State) => {
        batch(() => {
            dispatch(setPreviewDocumentAction(undefined));
            dispatch(setPreviewDocumentWithHighlightsAction(undefined));
            dispatch(searchResultSetExpandedDocumentAction(SearchPageType.BASIC, undefined));
            dispatch(changeInDocumentQueryAction(undefined));
            dispatch(setCurrentPageNumberAction(1));
        });
    };
};

